import { ProjectionEntity, ProjectionLog } from "../../projections/projectionEngine.js";
import { SharedLogSearchRequestQueue } from "../../utils/logSearchRequests.js";

const VoiceTimelineTypes = [
    "voice_join",
    "voice_leave",
    "voice_move",
    "voice_state_update",
    "voice_state_video",
    "voice_state_stream",
    "member_move",
    "member_disconnect",
    "utils_voice_join",
    "utils_voice_move",
] as const;

type LogSearchQueue = {
    fetchJson<TPayload extends Record<string, unknown>>(url: string, options?: RequestInit): Promise<TPayload>;
};

type VoiceTimelineResult = {
    logs: ProjectionLog[];
    members: ProjectionEntity[];
    channels: ProjectionEntity[];
};

type VoiceTimelineFetchResponse = {
    error?: string;
    result?: ProjectionLog[];
    members?: ProjectionEntity[];
    channels?: ProjectionEntity[];
};

type VoiceTimelineFetchOptions = {
    signal?: AbortSignal;
};

function readVoiceChannelId(log: ProjectionLog): string | null {
    return log.channel || log.to || log.from || null;
}

function createTimelineKey(log: ProjectionLog): string {
    return [
        log.id,
        log.type,
        log.createdAt,
        log.executor,
        log.target,
        log.channel,
        log.from,
        log.to,
    ].filter(Boolean).join(":");
}

function mergeLogs(logs: ProjectionLog[]): ProjectionLog[] {
    const byKey = new Map<string, ProjectionLog>();

    for (const log of logs)
        byKey.set(createTimelineKey(log), log);

    return [ ...byKey.values() ].sort((left, right) => right.createdAt - left.createdAt);
}

export class VoiceLogTimeline {
    readonly logSearchQueue: LogSearchQueue;
    readonly limit: number;
    readonly cache: Map<string, VoiceTimelineResult>;

    constructor({ logSearchQueue = SharedLogSearchRequestQueue, limit = 25 }: {
        logSearchQueue?: LogSearchQueue;
        limit?: number;
    } = {}) {
        this.logSearchQueue = logSearchQueue;
        this.limit = limit;
        this.cache = new Map();
    }

    getChannelId(log: ProjectionLog): string | null {
        return readVoiceChannelId(log);
    }

    async fetchAround(log: ProjectionLog, { signal }: VoiceTimelineFetchOptions = {}): Promise<VoiceTimelineResult> {
        const channelId = this.getChannelId(log);

        if (!channelId)
            return this.createEmptyResult();

        const cacheKey = `${channelId}:${log.createdAt}`;
        const cachedResult = this.cache.get(cacheKey);

        if (cachedResult)
            return cachedResult;

        const [ older, newer ] = await Promise.all([
            this.fetchPage({
                channelId,
                before  : log.createdAt + 1,
                orderBy : "DESC",
                signal,
            }),
            this.fetchPage({
                channelId,
                after   : Math.max(0, log.createdAt - 1),
                orderBy : "ASC",
                signal,
            }),
        ]);
        const result = this.mergeResults([ older, newer ]);

        this.cache.set(cacheKey, result);

        return result;
    }

    async fetchOlder(logs: ProjectionLog[], channelId: string | null, { signal }: VoiceTimelineFetchOptions = {}): Promise<VoiceTimelineResult> {
        const oldest = logs.at(-1);

        if (!oldest || !channelId)
            return this.createEmptyResult();

        return await this.fetchPage({
            channelId,
            before  : oldest.createdAt,
            orderBy : "DESC",
            signal,
        });
    }

    async fetchNewer(logs: ProjectionLog[], channelId: string | null, { signal }: VoiceTimelineFetchOptions = {}): Promise<VoiceTimelineResult> {
        const newest = logs[0];

        if (!newest || !channelId)
            return this.createEmptyResult();

        return await this.fetchPage({
            channelId,
            after   : newest.createdAt,
            orderBy : "ASC",
            signal,
        });
    }

    mergeResultInto(current: VoiceTimelineResult, next: VoiceTimelineResult): VoiceTimelineResult {
        return this.mergeResults([ current, next ]);
    }

    async fetchPage({
        channelId,
        before,
        after,
        orderBy,
        signal,
    }: {
        channelId: string;
        before?: number;
        after?: number;
        orderBy: "ASC" | "DESC";
        signal?: AbortSignal;
    }): Promise<VoiceTimelineResult> {
        const params = new URLSearchParams({
            limit   : String(this.limit),
            orderBy,
        });

        if (before)
            params.set("before", String(before));

        if (after)
            params.set("after", String(after));

        const response = await this.logSearchQueue.fetchJson<VoiceTimelineFetchResponse>(`/api/logs?${params.toString()}`, {
            headers: {
                "content-type": "application/json",
            },
            method  : "POST",
            signal,
            body    : JSON.stringify({
                types       : VoiceTimelineTypes,
                channels    : [ channelId ],
            }),
        });

        if (response.error)
            throw new Error(response.error);

        return {
            logs        : response.result ?? [],
            members     : response.members ?? [],
            channels    : response.channels ?? [],
        };
    }

    mergeResults(results: VoiceTimelineResult[]): VoiceTimelineResult {
        return {
            logs        : mergeLogs(results.flatMap((result) => result.logs)),
            members     : this.mergeEntities(results.flatMap((result) => result.members)),
            channels    : this.mergeEntities(results.flatMap((result) => result.channels)),
        };
    }

    mergeEntities(entities: ProjectionEntity[]): ProjectionEntity[] {
        const byId = new Map<string, ProjectionEntity>();

        for (const entity of entities)
            if (entity?.id)
                byId.set(entity.id, entity);

        return [ ...byId.values() ];
    }

    createEmptyResult(): VoiceTimelineResult {
        return {
            logs        : [],
            members     : [],
            channels    : [],
        };
    }
}

export function isVoiceTimelineLog(log: ProjectionLog | null | undefined): boolean {
    return Boolean(log && VoiceTimelineTypes.includes(log.type as typeof VoiceTimelineTypes[number]));
}
